// ─────────────────────────────────────────────────────────
// utils/deps.ts  —  Dependency Guard System
//
// Ported exactly from monolith DEP_MAP / _checkDep /
// _depGuard / _applyDepGuards / _checkSalaryAtt.
//
// Architecture (unchanged from monolith):
//   DEP_MAP: central registry of all module dependencies
//   checkDep(moduleKey, data): returns first missing dep or null
//   checkSalaryAtt(att, m, y): month-specific attendance check
//
// All 28 guarded modules preserved.
// Custom checkFn support preserved (used by salary module).
// ─────────────────────────────────────────────────────────
import { ak } from './format'
import type { AttSheet } from '@/types'

// ── Dependency descriptor ─────────────────────────────────
export interface DepDescriptor {
  key:       string          // D collection key
  label:     string          // Human-readable name for toast
  page:      string          // Route to navigate to
  isObj?:    boolean         // true for object collections (att, sal, boq)
  checkFn?:  string          // Name of custom check function
}

// ── DEP_MAP — exactly matches monolith ───────────────────
export const DEP_MAP: Record<string, DepDescriptor[]> = {
  boq:         [{ key: 'proj',    label: 'a Project',                  page: 'projects' }],
  receivables: [{ key: 'proj',    label: 'a Project',                  page: 'projects' }],
  expenses:    [{ key: 'proj',    label: 'a Project',                  page: 'projects' },
                { key: 'vendor',  label: 'a Vendor',                   page: 'vendors' }],
  cashflow:    [{ key: 'proj',    label: 'a Project',                  page: 'projects' }],
  invoices:    [{ key: 'vendor',  label: 'a Vendor',                   page: 'vendors' }],
  attendance:  [{ key: 'emp',     label: 'an Employee',                page: 'employees' },
                { key: 'proj',    label: 'a Project',                  page: 'projects' }],
  salary:      [{ key: 'emp',     label: 'an Employee',                page: 'employees' },
                // Fix B4/I5: attendance check is month-specific — checked via checkFn at runtime
                { key: 'att',     label: 'Attendance records for this month', page: 'attendance',
                  isObj: true, checkFn: '_checkSalaryAtt' }],
  advances:    [{ key: 'emp',     label: 'an Employee',                page: 'employees' }],
  insurance:   [{ key: 'proj',    label: 'a Project',                  page: 'projects' }],
  emd:         [{ key: 'tender',  label: 'a Tender',                   page: 'tender' }],
  dpr:         [{ key: 'proj',    label: 'a Project',                  page: 'projects' },
                { key: 'emp',     label: 'an Employee',                page: 'employees' }],
  pettycash:   [{ key: 'proj',    label: 'a Project',                  page: 'projects' }],
  po:          [{ key: 'proj',    label: 'a Project',                  page: 'projects' },
                { key: 'vendor',  label: 'a Vendor',                   page: 'vendors' }],
  milestones:  [{ key: 'proj',    label: 'a Project',                  page: 'projects' }],
  retention:   [{ key: 'proj',    label: 'a Project',                  page: 'projects' },
                { key: 'recv',    label: 'a Receivable (RA Bill)',      page: 'receivables' }],
  quotations:  [{ key: 'proj',    label: 'a Project',                  page: 'projects' }],
  einvoice:    [{ key: 'recv',    label: 'a Receivable (RA Bill)',      page: 'receivables' },
                { key: 'vendor',  label: 'a Vendor',                   page: 'vendors' }],
  tds:         [{ key: 'invoice', label: 'a Contractor Invoice',       page: 'invoices' }],
  bankrecon:   [{ key: 'invoice', label: 'a Contractor Invoice',       page: 'invoices' }],
  tasks:       [{ key: 'proj',    label: 'a Project',                  page: 'projects' }],
  // Independent modules — no deps
  employees:   [],
  projects:    [],
  vendors:     [],
  tender:      [],
}

// ── Data shape for dep checking ───────────────────────────
export interface DepData {
  proj:       unknown[]
  tender:     unknown[]
  emd:        unknown[]
  recv:       unknown[]
  exp:        unknown[]
  vendor:     unknown[]
  invoice:    unknown[]
  emp:        unknown[]
  att:        Record<string, AttSheet>
  task:       unknown[]
  [key: string]: unknown[] | Record<string, unknown>
}

// ── Month-specific attendance check for salary ────────────
// Replaces monolith _checkSalaryAtt()
// Returns true (missing) if no attendance recorded for selected month
export function checkSalaryAtt(
  att: Record<string, AttSheet>,
  m:   number,
  y:   number
): boolean {
  const key         = ak(m, y)
  const attForMonth = att[key]
  return !attForMonth || Object.keys(attForMonth).length === 0
}

// ── Valid depModule keys (for NavQuickAction.depModule) ──
export type DepMapKey =
  'boq' | 'receivables' | 'expenses' | 'cashflow' | 'invoices' | 'attendance' |
  'salary' | 'advances' | 'insurance' | 'emd' | 'dpr' | 'pettycash' | 'po' |
  'milestones' | 'retention' | 'quotations' | 'einvoice' | 'tds' | 'bankrecon' |
  'tasks' | 'employees' | 'projects' | 'vendors' | 'tender'

// ── Check deps for a module ───────────────────────────────
// Replaces monolith _checkDep(moduleKey)
// Returns null if all clear, or first missing DepDescriptor
export function checkDep(
  moduleKey: string,
  data:      DepData,
  // For salary: current month/year selection
  salaryMonth?: number,
  salaryYear?:  number,
): DepDescriptor | null {
  const deps = DEP_MAP[moduleKey]
  if (!deps || !deps.length) return null

  for (const dep of deps) {
    let empty: boolean

    if (dep.checkFn === '_checkSalaryAtt') {
      // Month-specific custom check
      const m = salaryMonth ?? new Date().getMonth()
      const y = salaryYear  ?? new Date().getFullYear()
      empty = checkSalaryAtt(data.att, m, y)
    } else {
      const col = data[dep.key]
      empty = dep.isObj
        ? (!col || Object.keys(col as object).length === 0)
        : (!col || (col as unknown[]).length === 0)
    }

    if (empty) return dep
  }

  return null
}
